import { Component } from '@theme/component';

/**
 * Split promo with exclusive accordion, synced product image, and dots.
 *
 * @typedef {object} Refs
 * @property {HTMLImageElement} [image]
 * @property {HTMLElement[]} [headers]
 * @property {HTMLElement[]} [panels]
 * @property {HTMLButtonElement[]} [dots]
 *
 * @extends {Component<Refs>}
 */
class MgPromoAccordionComponent extends Component {
  /** @type {string} */
  #defaultSrc = '';

  /** @type {string} */
  #defaultAlt = '';

  /** @type {string} */
  #defaultSrcset = '';

  /** @type {string} */
  #defaultSizes = '';

  /** @type {number} */
  #openIndex = -1;

  /** @type {string} */
  #desktopSrc = '';

  /** @type {string} */
  #desktopAlt = '';

  /** @type {string} */
  #desktopSrcset = '';

  /** @type {string} */
  #desktopSizes = '';

  /** @type {string} */
  #mobileSrc = '';

  /** @type {string} */
  #mobileAlt = '';

  /** @type {MediaQueryList | null} */
  #viewportQuery = null;

  /** @type {() => void} */
  #onViewportChange = () => {};

  connectedCallback() {
    super.connectedCallback();

    const { image } = this.refs;
    if (image instanceof HTMLImageElement) {
      this.#desktopSrc = image.currentSrc || image.src || '';
      this.#desktopAlt = image.alt || '';
      this.#desktopSrcset = image.getAttribute('srcset') || '';
      this.#desktopSizes = image.getAttribute('sizes') || '';

      image.addEventListener(
        'error',
        () => {
          this.#desktopSrc = '';
          this.#desktopSrcset = '';
          this.#desktopSizes = '';
          this.#defaultSrc = '';
          this.#defaultSrcset = '';
          this.#defaultSizes = '';
          this.#showImage(this.#openIndex);
        },
        { once: true }
      );
    }

    const media = this.querySelector('.mg-promo-accordion__media');
    this.#mobileSrc = media?.dataset.defaultSrcMobile || '';
    this.#mobileAlt = media?.dataset.defaultAltMobile || this.#desktopAlt;
    this.#viewportQuery = window.matchMedia('(max-width: 767px)');
    this.#onViewportChange = () => {
      this.#applyViewportDefault();
      this.#showImage(this.#openIndex);
    };
    this.#viewportQuery.addEventListener('change', this.#onViewportChange);
    this.#applyViewportDefault();
    this.#syncFromDom();
  }

  disconnectedCallback() {
    this.#viewportQuery?.removeEventListener('change', this.#onViewportChange);
    super.disconnectedCallback();
  }

  /**
   * Accordion header click — exclusive open/close.
   * @param {Event} event
   */
  toggleItem = (event) => {
    event.preventDefault();
    const header = event.target;
    if (!(header instanceof HTMLElement)) return;

    const index = this.#headerIndex(header);
    if (index < 0) return;

    const isOpen = header.getAttribute('aria-expanded') === 'true';
    this.#setOpenIndex(isOpen ? -1 : index);
  };

  /**
   * Dot click — force-open matching item.
   * @param {Event} event
   */
  selectDot = (event) => {
    event.preventDefault();
    const dot = event.target;
    if (!(dot instanceof HTMLElement)) return;

    const index = Number(dot.dataset.index);
    if (Number.isNaN(index)) return;

    this.#setOpenIndex(index);
  };

  /**
   * @param {number} openIndex - Item index to open, or -1 to close all
   */
  #setOpenIndex(openIndex) {
    this.#openIndex = openIndex;
    const headers = this.#headers();
    const panels = this.#panels();
    const dots = this.#dots();

    headers.forEach((header, index) => {
      const open = index === openIndex;
      const panel = panels[index];
      const icon = header.querySelector('.mg-promo-accordion__icon');

      header.setAttribute('aria-expanded', open ? 'true' : 'false');

      if (panel instanceof HTMLElement) {
        panel.setAttribute('aria-hidden', open ? 'false' : 'true');
        if (open) {
          panel.removeAttribute('inert');
          panel.style.maxHeight = `${panel.scrollHeight}px`;
        } else {
          panel.setAttribute('inert', '');
          panel.style.maxHeight = '0px';
        }
      }

      if (icon instanceof HTMLElement) {
        icon.textContent = open ? '−' : '+';
      }
    });

    dots.forEach((dot, index) => {
      const active = index === openIndex;
      dot.classList.toggle('is-active', active);
      if (active) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });

    this.#showImage(openIndex);
  }

  /**
   * @param {number} index
   */
  #showImage(index) {
    const { image } = this.refs;
    if (!(image instanceof HTMLImageElement)) return;

    const placeholder = this.querySelector('.mg-promo-accordion__placeholder');

    if (index < 0) {
      if (this.#defaultSrc) {
        image.src = this.#defaultSrc;
        image.alt = this.#defaultAlt;
        if (this.#defaultSrcset) {
          image.setAttribute('srcset', this.#defaultSrcset);
        } else {
          image.removeAttribute('srcset');
        }
        if (this.#defaultSizes) {
          image.setAttribute('sizes', this.#defaultSizes);
        } else {
          image.removeAttribute('sizes');
        }
        image.classList.remove('mg-promo-accordion__image--hidden');
        placeholder?.classList.add('mg-promo-accordion__image--hidden');
      } else {
        image.removeAttribute('src');
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.alt = '';
        image.classList.add('mg-promo-accordion__image--hidden');
        placeholder?.classList.remove('mg-promo-accordion__image--hidden');
      }
      return;
    }

    const header = this.#headers()[index];
    const mobileImage = this.#isMobile() && header?.dataset.imageSrcMobile;
    const src = mobileImage ? header.dataset.imageSrcMobile : header?.dataset.imageSrc || '';
    const alt = mobileImage
      ? header.dataset.imageAltMobile || header.dataset.imageAlt || this.#defaultAlt
      : header?.dataset.imageAlt || header?.dataset.dotLabel || this.#defaultAlt;

    if (src) {
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = src;
      image.alt = alt;
      image.classList.remove('mg-promo-accordion__image--hidden');
      placeholder?.classList.add('mg-promo-accordion__image--hidden');
    } else if (this.#defaultSrc) {
      image.src = this.#defaultSrc;
      image.alt = this.#defaultAlt;
      if (this.#defaultSrcset) {
        image.setAttribute('srcset', this.#defaultSrcset);
      } else {
        image.removeAttribute('srcset');
      }
      if (this.#defaultSizes) {
        image.setAttribute('sizes', this.#defaultSizes);
      } else {
        image.removeAttribute('sizes');
      }
      image.classList.remove('mg-promo-accordion__image--hidden');
      placeholder?.classList.add('mg-promo-accordion__image--hidden');
    } else {
      image.removeAttribute('src');
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.alt = '';
      image.classList.add('mg-promo-accordion__image--hidden');
      placeholder?.classList.remove('mg-promo-accordion__image--hidden');
    }
  }

  #isMobile() {
    return this.#viewportQuery?.matches === true;
  }

  #applyViewportDefault() {
    if (this.#isMobile() && this.#mobileSrc) {
      this.#defaultSrc = this.#mobileSrc;
      this.#defaultAlt = this.#mobileAlt;
      this.#defaultSrcset = '';
      this.#defaultSizes = '';
      return;
    }

    this.#defaultSrc = this.#desktopSrc;
    this.#defaultAlt = this.#desktopAlt;
    this.#defaultSrcset = this.#desktopSrcset;
    this.#defaultSizes = this.#desktopSizes;
  }

  #syncFromDom() {
    const headers = this.#headers();
    const openIndex = headers.findIndex((header) => header.getAttribute('aria-expanded') === 'true');
    this.#setOpenIndex(openIndex);
  }

  /**
   * @param {HTMLElement} header
   * @returns {number}
   */
  #headerIndex(header) {
    const button =
      header instanceof HTMLElement ? header.closest('.mg-promo-accordion__header') || header : null;
    if (!(button instanceof HTMLElement)) return -1;
    return this.#headers().indexOf(button);
  }

  /** @returns {HTMLElement[]} */
  #headers() {
    const { headers } = this.refs;
    return Array.isArray(headers) ? headers.filter((el) => el instanceof HTMLElement) : [];
  }

  /** @returns {HTMLElement[]} */
  #panels() {
    const { panels } = this.refs;
    return Array.isArray(panels) ? panels.filter((el) => el instanceof HTMLElement) : [];
  }

  /** @returns {HTMLButtonElement[]} */
  #dots() {
    const { dots } = this.refs;
    return Array.isArray(dots) ? dots.filter((el) => el instanceof HTMLButtonElement) : [];
  }
}

if (!customElements.get('mg-promo-accordion-component')) {
  customElements.define('mg-promo-accordion-component', MgPromoAccordionComponent);
}
